import { FieldTimeOutlined, FilterOutlined, ToolOutlined } from '@ant-design/icons';
import { Button, Card, Col, InputNumber, Row, Select, Table, Tag, Tooltip, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { updatePmOfferProjectWorkPlaceItem } from '../../../../../../contextRedux/PMOfferProject/PmOfferModelProject';
import { RootState } from '../../../../../../store';
import { Column } from '../../../../../../utils/column';
import './DevicesTable.css';

const { Option } = Select;
const { Title, Text } = Typography;

const ProjectDevicesTable: React.FC<any> = () => {
  const isMobile = useMediaQuery({ query: '(max-width: 1003px)' });
  const dispatch = useDispatch();
  const addedDevices = useSelector((state: RootState) => state?.models?.pmOfferProject.pmOfferWorkPlaceItems);
  const pmOffer = useSelector((state: RootState) => state?.models?.pmOfferProject);

  // Filter states
  const [filters, setFilters] = useState<any>({
    workPlace: [],
    stage: [],
    device: [],
    unitMeasure: [],
  });

  // Filtered data state
  const [filteredData, setFilteredData] = useState<any[]>([]);

  // Extract unique filter options
  const filterOptions = useMemo(() => {
    const options = {
      workPlace: new Set<string>(),
      stage: new Set<string>(),
      device: new Set<string>(),
      unitMeasure: new Set<string>(),
    };

    addedDevices?.forEach((device: any) => {
      if (device?.workPlace?.name) options.workPlace.add(device.workPlace.name);
      if (device?.pmOfferWorkPlaceItemStage?.name) options.stage.add(device.pmOfferWorkPlaceItemStage.name);
      if (device?.device?.name) options.device.add(device.device.name);
      if (device?.unitMeasure) options.unitMeasure.add(device.unitMeasure);
    });

    return {
      workPlace: Array.from(options.workPlace),
      stage: Array.from(options.stage),
      device: Array.from(options.device),
      unitMeasure: Array.from(options.unitMeasure),
    };
  }, [addedDevices]);

  // Apply filters to data
  useEffect(() => {
    let result: any[] = [...addedDevices];

    // Apply workPlace filter
    if (filters.workPlace.length > 0) {
      result = result.filter((device) => filters.workPlace.includes(device?.workPlace?.name));
    }

    // Apply stage filter
    if (filters.stage.length > 0) {
      result = result.filter((device) => filters.stage.includes(device?.pmOfferWorkPlaceItemStage?.name));
    }

    // Apply device filter
    if (filters.device.length > 0) {
      result = result.filter((device) => filters.device.includes(device?.device?.name));
    }

    // Apply unitMeasure filter
    if (filters.unitMeasure.length > 0) {
      result = result.filter((device) => filters.unitMeasure.includes(device?.unitMeasure));
    }

    setFilteredData(result);
  }, [addedDevices, filters]);

  // Calculate totals for all filtered devices
  const calculateTotals = useCallback(() => {
    const totalPieces = Number.parseFloat(filteredData.reduce((total, item) => total + item.piece, 0).toFixed(2));
    const totalLaborPrice = Number.parseFloat(
      filteredData.reduce((total, item) => total + item.laborPrice, 0).toFixed(2)
    );
    const totalLaborTime = filteredData.reduce((total, item) => total + item.laborTime, 0);

    return { totalPieces, totalLaborPrice, totalLaborTime };
  }, [filteredData]);

  const handleDeviceChange = (record: any, value: any, dataIndex: string) => {
    const updatedRecord = { ...record, [dataIndex]: value };

    dispatch(
      updatePmOfferProjectWorkPlaceItem({
        index: addedDevices.findIndex(
          (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
        ),
        updates: updatedRecord,
      })
    );
  };

  const handleUnitChange = (record: any, newUnit: string) => {
    // Find the index of the record in addedDevices
    const recordIndex = addedDevices.findIndex(
      (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
    );

    // If the record is found, proceed with the update
    if (recordIndex !== -1) {
      const updatedRecord = { ...addedDevices[recordIndex] };

      if (newUnit === 'hour') {
        updatedRecord.laborTime = Number.parseFloat((60 * updatedRecord.piece)?.toFixed(2));
        updatedRecord.laborPrice = Number.parseFloat(
          (pmOffer.laborTab.laborPriceOnHour ?? 300 * updatedRecord.piece)?.toFixed(2)
        );
        updatedRecord.laborPriceOnHour = Number.parseFloat((pmOffer.laborTab.laborPriceOnHour ?? 300).toFixed(2));
      } else {
        const device = updatedRecord.device ? { ...updatedRecord.device } : {};

        updatedRecord.laborTime = Number.parseFloat(
          ((device.laborTimePM + (device.totalLaborsTimePM ?? 0)) * updatedRecord.piece)?.toFixed(2)
        );
        updatedRecord.laborPrice = Number.parseFloat(
          ((device.laborPricePM + (device.totalLaborsPricePM ?? 0)) * updatedRecord.piece)?.toFixed(2)
        );
      }

      // Update the unit measure
      updatedRecord.unitMeasure = newUnit;

      // Dispatch the update for the specific record in the Redux state
      dispatch(
        updatePmOfferProjectWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
          ),
          updates: updatedRecord,
        })
      );
    }
  };

  const renderColumn = (column: any, _text: string, record: any) => {
    let value;
    if (Array.isArray(column.dataIndex)) {
      value = column.dataIndex.reduce((acc: any, key: any) => {
        // Ensure `acc` is not undefined and `key` exists in the `acc` object
        return acc && acc[key] !== undefined ? acc[key] : undefined;
      }, record);
    } else {
      value = record[column.dataIndex];
    }
    if (column.editable) {
      return (
        <div className='diableButton-input-container'>
          {column.dataIndex === 'laborPrice' && (
            <Button disabled className='disableButton'>
              <b>
                <ToolOutlined
                  style={{
                    marginRight: 5,
                  }}
                />
                {record.unitMeasure == 'buc'
                  ? Number.parseFloat((record.laborPrice / record.piece).toFixed(2)) + ' lei/buc'
                  : Number.parseFloat((record.laborPrice / record.piece).toFixed(2)) + ' lei/ora'}
              </b>
            </Button>
          )}
          {column.dataIndex === 'laborTime' && (
            <Button disabled className='disableButton'>
              <b>
                <FieldTimeOutlined
                  style={{
                    marginRight: 5,
                  }}
                />
                {record.unitMeasure == 'buc' ? record.device.laborTimePM + ' min/buc' : ''}
              </b>
            </Button>
          )}
          <InputNumber
            min={1}
            value={value}
            disabled={column.dataIndex === 'piece'}
            onChange={(value) => handleDeviceChange(record, value, column.dataIndex)}
            className='input-field'
          />
          {column.dataIndex === 'piece' && (
            <Select
              value={record?.unitMeasure || 'buc'}
              onChange={(newUnit: string) => handleUnitChange(record, newUnit)}
              className='input-field'
            >
              <Option value='buc'>buc</Option>
              <Option value='hour'>hour</Option>
            </Select>
          )}
        </div>
      );
    }
    return value;
  };

  const calculateWeightedAverage = (unitMeasure: string, devices: any[]) => {
    let sumWeightedCosts = 0;
    let laborTimePerPieces = 0;

    devices
      .filter((ad) => ad.unitMeasure === unitMeasure)
      .forEach((item) => {
        const { laborTime, laborPrice, piece } = item;
        sumWeightedCosts += Number.parseFloat((piece * (((laborPrice / piece) * 60) / (laborTime / piece))).toFixed(2));
        laborTimePerPieces += piece;
      });
    return Number.parseFloat((sumWeightedCosts / laborTimePerPieces).toFixed(2)) || 0;
  };

  // Handle filter changes
  const handleFilterChange = (filterType: string, values: string[]) => {
    setFilters((prev: any) => ({
      ...prev,
      [filterType]: values,
    }));
  };

  // Create filter dropdown menu for a column
  const createFilterDropdown = (filterType: string, options: string[]) => {
    return {
      filterDropdown: ({ confirm }: any) => (
        <div style={{ padding: 8, minWidth: 200 }}>
          <Select
            mode='multiple'
            style={{ width: '100%' }}
            placeholder={`Filter by ${filterType}`}
            value={filters[filterType as keyof typeof filters] as string[]}
            onChange={(values) => {
              handleFilterChange(filterType, values);
              confirm();
            }}
            options={options.map((option) => ({ value: option, label: option }))}
            maxTagCount={2}
          />
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              size='small'
              onClick={() => {
                handleFilterChange(filterType, []);
                confirm();
              }}
            >
              Reset
            </Button>
            <Button type='primary' size='small' onClick={() => confirm()}>
              Apply
            </Button>
          </div>
        </div>
      ),
      filterIcon: (filtered: boolean) => <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      filtered: filters[filterType as keyof typeof filters].length > 0,
    };
  };

  const columnsFirstTable: Column[] = [
    {
      title: 'No.',
      key: 'index',
      dataIndex: 'index',
      render: (_text: any, _record: any, index: any) => <div>{index + 1 + '.'}</div>,
      width: 60,
    },
    {
      title: 'Locație',
      dataIndex: ['workPlace', 'name'],
      key: 'workPlace',
      ...createFilterDropdown('workPlace', filterOptions.workPlace),
      render: (text: string) => (
        <Tag color='blue' className='location-badge'>
          {text}
        </Tag>
      ),
    },
    {
      title: 'Etapă',
      dataIndex: ['pmOfferWorkPlaceItemStage', 'name'],
      key: 'stage',
      ...createFilterDropdown('stage', filterOptions.stage),
      render: (text: string) => (
        <Tag color='green' className='stage-badge'>
          {text}
        </Tag>
      ),
    },
    {
      title: 'Device',
      key: 'device',
      ...createFilterDropdown('device', filterOptions.device),
      width: 200,
      render: (record) => (
        <div style={{ display: 'grid', gap: 8 }}>
          <Tooltip title={record.device?.name}>
            <div className='ellipsis-cell'>
              <Text strong>{record?.device?.name}</Text>
            </div>
          </Tooltip>
          {record?.device?.imageLink &&
          record?.device?.imageLink[0] != '' &&
          record?.device?.imageLink[0].includes('.jpg', '.png') ? (
            <div className='device-image-container'>
              <img src={record?.device?.imageLink[0]} alt='' className='device-image' />
            </div>
          ) : (
            ''
          )}
        </div>
      ),
      dataIndex: '',
    },
    {
      title: 'Cantitate',
      dataIndex: 'piece',
      key: 'piece',
      editable: true,
    },
    {
      title: 'Unitate De Masura',
      dataIndex: 'unitMeasure',
      key: 'unitMeasure',
      ...createFilterDropdown('unitMeasure', filterOptions.unitMeasure),
      render: (text: string) => <Tag color={text === 'buc' ? 'volcano' : 'geekblue'}>{text}</Tag>,
    },
    {
      title: 'Cost Manopera Per Bucata (lei)',
      dataIndex: 'laborPrice',
      key: 'laborPrice',
      editable: true,
    },
    {
      title: 'Timp Manopera Estimat (minute)',
      dataIndex: 'laborTime',
      key: 'laborTime',
      editable: true,
    },
  ];

  //Mobile view render card
  const renderCard = (record: any) => (
    <Card
      key={record.id}
      className='deviceCard'
      title={
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <Tag color='blue' className='location-badge'>
            {record.workPlace?.name}
          </Tag>
          <Tag color='green' className='stage-badge'>
            {record.pmOfferWorkPlaceItemStage?.name}
          </Tag>
        </div>
      }
    >
      {record?.device?.imageLink && record?.device?.imageLink[0]?.includes('.jpg', '.png') && (
        <div className='device-image-container-mobile'>
          <img src={record.device.imageLink[0]} alt='device' className='device-image-mobile' />
        </div>
      )}

      <Tooltip title={record.device?.name}>
        <div className='ellipsisCell'>
          <Text strong>Device: </Text> {record.device?.name}
        </div>
      </Tooltip>

      <div className='input-container'>
        <div className='input-label'>Cantitate:</div>
        <div className='input-group'>
          <Button disabled className='disableButton'>
            <b>{record.piece}</b>
          </Button>
          <Select
            value={record?.unitMeasure || 'buc'}
            onChange={(newUnit: string) => handleUnitChange(record, newUnit)}
            className='input-field'
          >
            <Option value='buc'>buc</Option>
            <Option value='hour'>hour</Option>
          </Select>
        </div>
      </div>

      <div className='input-container'>
        <div className='input-label'>Cost Manopera:</div>
        <div className='input-group'>
          <Button disabled className='disableButton'>
            <ToolOutlined style={{ marginRight: 5 }} />
            <span>
              {record.unitMeasure === 'buc'
                ? record.device.laborPriceSM + ' lei/buc'
                : Number.parseFloat((record.laborPrice / record.piece).toFixed(2)) + ' lei/ora'}
            </span>
          </Button>
          <InputNumber
            min={1}
            value={record.laborPrice}
            onChange={(value) => handleDeviceChange(record, value, 'laborPrice')}
            className='input-field'
          />
        </div>
      </div>

      <div className='input-container'>
        <div className='input-label'>Timp Manopera:</div>
        <div className='input-group'>
          <Button disabled className='disableButton'>
            <FieldTimeOutlined style={{ marginRight: 5 }} />
            <span>{record.unitMeasure === 'buc' ? record.device.laborTimeSM + ' min/buc' : '60 min'}</span>
          </Button>
          <InputNumber
            min={1}
            value={record.laborTime}
            onChange={(value) => handleDeviceChange(record, value, 'laborTime')}
            className='input-field'
          />
        </div>
      </div>
    </Card>
  );

  // Render summary for all filtered devices
  const renderSummary = () => {
    const { totalPieces, totalLaborPrice, totalLaborTime } = calculateTotals();

    const bucDevices = filteredData.filter((d) => d.unitMeasure === 'buc');
    const hourDevices = filteredData.filter((d) => d.unitMeasure === 'hour');

    return (
      <Table.Summary>
        {bucDevices.length > 0 && (
          <Table.Summary.Row className='summary-row'>
            <Table.Summary.Cell index={0} colSpan={4}>
              <Text strong>Manopera bucata/ora</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4} colSpan={2}>
              <Text strong style={{ color: '#1890ff' }}>
                {calculateWeightedAverage('buc', bucDevices)} lei
              </Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6} colSpan={2} />
          </Table.Summary.Row>
        )}
        {hourDevices.length > 0 && (
          <Table.Summary.Row className='summary-row'>
            <Table.Summary.Cell index={0} colSpan={4}>
              <Text strong>Manopera/ora</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4} colSpan={2}>
              <Text strong style={{ color: '#1890ff' }}>
                {calculateWeightedAverage('hour', hourDevices)} lei
              </Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6} colSpan={2} />
          </Table.Summary.Row>
        )}
        <Table.Summary.Row className='summary-row'>
          <Table.Summary.Cell index={0} colSpan={4}>
            <Text strong>Total</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4}>
            <Text strong>{totalPieces}</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} />
          <Table.Summary.Cell index={6} colSpan={1}>
            <Text strong style={{ color: '#52c41a' }}>
              {totalLaborPrice} lei
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={7}>
            <Text strong style={{ color: '#722ed1' }}>
              {totalLaborTime} minute
            </Text>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  // Render mobile summary
  const renderMobileSummary = () => {
    const { totalPieces, totalLaborPrice, totalLaborTime } = calculateTotals();

    const bucDevices = filteredData.filter((d) => d.unitMeasure === 'buc');
    const hourDevices = filteredData.filter((d) => d.unitMeasure === 'hour');

    return (
      <Card className='summary-card'>
        <Title level={5} style={{ marginBottom: 16 }}>
          Sumar Manoperă
        </Title>
        <Row gutter={[16, 16]} className='summary-row'>
          {bucDevices.length > 0 && (
            <Col span={24} className='summary-item'>
              <Text strong>Manopera bucata/ora: </Text>
              <Text style={{ color: '#1890ff', fontWeight: 'bold' }}>
                {calculateWeightedAverage('buc', bucDevices)} lei
              </Text>
            </Col>
          )}
          {hourDevices.length > 0 && (
            <Col span={24} className='summary-item'>
              <Text strong>Manopera/ora: </Text>
              <Text style={{ color: '#1890ff', fontWeight: 'bold' }}>
                {calculateWeightedAverage('hour', hourDevices)} lei
              </Text>
            </Col>
          )}
          <Col span={24} className='summary-item'>
            <Text strong>Total Cantitate: </Text>
            <Text style={{ fontWeight: 'bold' }}>{totalPieces}</Text>
          </Col>
          <Col span={24} className='summary-item'>
            <Text strong>Total Cost Manopera: </Text>
            <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>{totalLaborPrice} lei</Text>
          </Col>
          <Col span={24} className='summary-item'>
            <Text strong>Total Timp Manopera: </Text>
            <Text style={{ color: '#722ed1', fontWeight: 'bold' }}>{totalLaborTime} minute</Text>
          </Col>
        </Row>
      </Card>
    );
  };

  // Create a component for the filter section
  const renderFilterSection = () => {
    const hasActiveFilters = Object.values(filters).some((filter: any) => filter.length > 0);

    return (
      <>
        <div className='filter-container'>
          <Title level={5} style={{ marginBottom: 16, width: '100%' }}>
            Filtrează Dispozitivele
          </Title>

          <div className='filter-item'>
            <Text strong>Locație</Text>
            <Select
              mode='multiple'
              style={{ width: '100%', marginTop: 8 }}
              placeholder='Filtrează după locație'
              value={filters.workPlace}
              onChange={(values) => handleFilterChange('workPlace', values)}
              options={filterOptions.workPlace.map((option) => ({ value: option, label: option }))}
              maxTagCount={2}
              allowClear
            />
          </div>

          <div className='filter-item'>
            <Text strong>Etapă</Text>
            <Select
              mode='multiple'
              style={{ width: '100%', marginTop: 8 }}
              placeholder='Filtrează după etapă'
              value={filters.stage}
              onChange={(values) => handleFilterChange('stage', values)}
              options={filterOptions.stage.map((option) => ({ value: option, label: option }))}
              maxTagCount={2}
              allowClear
            />
          </div>

          <div className='filter-item'>
            <Text strong>Unitate de Măsură</Text>
            <Select
              mode='multiple'
              style={{ width: '100%', marginTop: 8 }}
              placeholder='Filtrează după unitate'
              value={filters.unitMeasure}
              onChange={(values) => handleFilterChange('unitMeasure', values)}
              options={filterOptions.unitMeasure.map((option) => ({
                value: option,
                label: option === 'buc' ? 'Bucată' : 'Oră',
              }))}
              maxTagCount={2}
              allowClear
            />
          </div>
        </div>

        {hasActiveFilters && (
          <div className='active-filters'>
            <Text strong>Filtre active:</Text>

            {filters.workPlace.length > 0 &&
              filters.workPlace.map((filter: string) => (
                <Tag
                  key={`workPlace-${filter}`}
                  closable
                  onClose={() => {
                    const newFilters = filters.workPlace.filter((f: string) => f !== filter);
                    handleFilterChange('workPlace', newFilters);
                  }}
                  color='blue'
                  className='filter-tag'
                >
                  Locație: {filter}
                </Tag>
              ))}

            {filters.stage.length > 0 &&
              filters.stage.map((filter: string) => (
                <Tag
                  key={`stage-${filter}`}
                  closable
                  onClose={() => {
                    const newFilters = filters.stage.filter((f: string) => f !== filter);
                    handleFilterChange('stage', newFilters);
                  }}
                  color='green'
                  className='filter-tag'
                >
                  Etapă: {filter}
                </Tag>
              ))}

            {filters.device.length > 0 &&
              filters.device.map((filter: string) => (
                <Tag
                  key={`device-${filter}`}
                  closable
                  onClose={() => {
                    const newFilters = filters.device.filter((f: string) => f !== filter);
                    handleFilterChange('device', newFilters);
                  }}
                  color='geekblue'
                  className='filter-tag'
                >
                  Device: {filter}
                </Tag>
              ))}

            {filters.unitMeasure.length > 0 &&
              filters.unitMeasure.map((filter: string) => (
                <Tag
                  key={`unitMeasure-${filter}`}
                  closable
                  onClose={() => {
                    const newFilters = filters.unitMeasure.filter((f: string) => f !== filter);
                    handleFilterChange('unitMeasure', newFilters);
                  }}
                  color={filter === 'buc' ? 'volcano' : 'geekblue'}
                  className='filter-tag'
                >
                  Unitate: {filter}
                </Tag>
              ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className='devices-table-container'>
      {/* Filter section */}
      {renderFilterSection()}

      {/* Filter reset button */}
      {Object.values(filters).some((filter: any) => filter.length > 0) && (
        <Button
          type='primary'
          onClick={() =>
            setFilters({
              workPlace: [],
              stage: [],
              device: [],
              unitMeasure: [],
            })
          }
          style={{ marginBottom: 16 }}
        >
          Reset All Filters
        </Button>
      )}

      {/* Unified table with all devices */}
      {isMobile ? (
        <div className='mobile-cards-container'>
          {filteredData.map(renderCard)}
          {renderMobileSummary()}
        </div>
      ) : (
        <Table
          rowClassName={(record) => (record.unitMeasure === 'hour' ? 'hour-row' : 'table-row-dark')}
          dataSource={filteredData}
          columns={columnsFirstTable.map((column, index) => ({
            ...column,
            key: index,
            render: column.render || ((text, record) => renderColumn(column, text, record)),
          }))}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          summary={() => renderSummary()}
        />
      )}
    </div>
  );
};

export default ProjectDevicesTable;
